import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { useImageStore } from '@/src/store/datasets-images';
import { useModelStore } from '@/src/store/datasets-models';
import { FILE_READERS } from '@/src/io';
import {
  ImportHandler,
  asLoadableResult,
  asOkayResult,
} from '@/src/io/import/common';
import { Skip } from '@/src/utils/evaluateChain';
import {
  isNiftiDataSource,
  queuePendingSegmentation,
  requestNiftiImportIntent,
} from '@/src/io/import/niftiImportIntent';
import { prepareForNiftiVolumeImport } from '@/src/actions/prepareVolumeLoad';
import { ScanLoadCancelledError } from '@/src/actions/scanLoadCancelled';

/**
 * Reads and imports a file DataSource.
 * @param dataSource
 * @returns
 */
const importSingleFile: ImportHandler = async (dataSource) => {
  if (dataSource.type !== 'file') {
    return Skip;
  }

  if (!FILE_READERS.has(dataSource.fileType)) {
    return Skip;
  }

  const reader = FILE_READERS.get(dataSource.fileType)!;
  const dataObject = await reader(dataSource.file);

  if (dataObject.isA('vtkImageData')) {
    if (isNiftiDataSource(dataSource)) {
      const intent = await requestNiftiImportIntent(dataSource.file.name);
      if (intent === 'segmentation') {
        queuePendingSegmentation({
          dataSource,
          fileName: dataSource.file.name,
          imageData: dataObject as vtkImageData,
        });
        return asOkayResult(dataSource);
      }

      if (!(await prepareForNiftiVolumeImport())) {
        throw new ScanLoadCancelledError();
      }
    }

    const dataID = useImageStore().addVTKImageData(
      dataSource.file.name,
      dataObject as vtkImageData
    );

    return asLoadableResult(dataID, dataSource, 'image');
  }

  if (dataObject.isA('vtkPolyData')) {
    const dataID = useModelStore().addVTKPolyData(
      dataSource.file.name,
      dataObject as vtkPolyData
    );

    return asLoadableResult(dataID, dataSource, 'model');
  }

  throw new Error(
    `Failed to import "${dataSource.file.name}". The file may be corrupted or in an unsupported format variant.`
  );
};

export default importSingleFile;
